import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { FaClock } from 'react-icons/fa'
import CustomDialog from '../../../components/CustomDialog'

const formatDateInput = (value) => {
  if (!value) {
    return ''
  }

  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

const formatTimeInput = (value) => {
  if (!value) {
    return ''
  }

  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('HH:mm') : ''
}

const UpdateEventTimeDialog = ({
  open = false,
  onClose = () => {},
  onSubmit = () => {},
  event = null,
  isProcessing = false,
}) => {
  const [dateValue, setDateValue] = useState('')
  const [timeValue, setTimeValue] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) {
      return
    }

    setDateValue(formatDateInput(event?.startTime))
    setTimeValue(formatTimeInput(event?.startTime))
    setError('')
  }, [event?.startTime, open])

  const description = useMemo(() => {
    if (!event) {
      return ''
    }

    return `${event.leagueName ?? 'Unknown League'} | ${event.homeTeam ?? 'Home'} vs ${event.awayTeam ?? 'Away'}`
  }, [event])

  const handleClose = () => {
    if (isProcessing) {
      return
    }

    setError('')
    onClose()
  }

  const handleSubmit = () => {
    if (!dateValue || !timeValue) {
      setError('Both date and time are required.')
      return
    }

    const nextDateTime = moment(`${dateValue} ${timeValue}`, 'YYYY-MM-DD HH:mm', true)

    if (!nextDateTime.isValid()) {
      setError('Please enter a valid kickoff date and time.')
      return
    }

    setError('')
    onSubmit(nextDateTime)
  }

  return (
    <CustomDialog open={open} width={520} padding={0}>
      <div className="overflow-hidden bg-white text-slate-900 dark:bg-custom_dark_primary dark:text-customLightMilkWhite">
        <div className="border-b border-slate-200 bg-customSlate px-6 py-4 dark:border-custom_dark_secondary dark:bg-custom_dark_primary2">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-customAccent/10 text-customAccent">
              <FaClock size={18} />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Update Kickoff Time</h2>
              <p className="text-sm text-customFontWhite dark:text-customMuted">Choose the valid date and time for this event.</p>
            </div>
          </div>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50/90 p-4 dark:border-custom_dark_secondary dark:bg-custom_dark_primary2/80">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-customMuted">Event Info</p>
            <p className="mt-2 text-sm font-semibold">{description}</p>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500 dark:text-customMuted italic">
              <span>Event ID: <span className="text-customAccentBlue dark:text-customAccent">{event?.eventId ?? '-'}</span></span>
              <span>Current: <span className="text-customAccentBlue dark:text-customAccent">{event?.startTime ? moment(event.startTime).format('YYYY-MM-DD hh:mm A') : '-'}</span></span>
              <span>Status: <span className="text-customAccentBlue dark:text-customAccent">{event?.status ?? '-'}</span></span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-2">
              <span className="text-sm font-semibold">Date</span>
              <input
                type="date"
                value={dateValue}
                onChange={(inputEvent) => setDateValue(inputEvent.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-customAccentBlue focus:ring-2 focus:ring-customAccentBlue/30 dark:border-custom_dark_secondary dark:bg-custom_dark_primary2"
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-sm font-semibold">Time</span>
              <input
                type="time"
                value={timeValue}
                onChange={(inputEvent) => setTimeValue(inputEvent.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-customAccentBlue focus:ring-2 focus:ring-customAccentBlue/30 dark:border-custom_dark_secondary dark:bg-custom_dark_primary2"
              />
            </label>
          </div>

          {error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-300">
              {error}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:justify-end dark:border-custom_dark_secondary">
          <button
            type="button"
            onClick={handleClose}
            disabled={isProcessing}
            className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-custom_dark_secondary dark:text-customLightMilkWhite dark:hover:bg-custom_dark_primary2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isProcessing}
            className="inline-flex items-center justify-center rounded-full bg-customAccentBlue px-5 py-2 text-sm font-semibold text-white transition hover:bg-customAccentBlue/90 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-customAccent dark:hover:bg-customAccent/90"
          >
            {isProcessing ? 'Updating...' : 'Update Time'}
          </button>
        </div>
      </div>
    </CustomDialog>
  )
}

export default UpdateEventTimeDialog
